// src/components/AppTopbar.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCompany } from "../context/CompanyContext";
import { Crown, X, ArrowRight } from "lucide-react";
import NotificationsBell from "../components/NotificationsBell";
import "./AppTopbar.css";

const UPGRADE_PROMPT_KEY = "kp_upgrade_prompt_last"; // localStorage key
const PROMPT_INTERVAL_MS = 24 * 60 * 60 * 1000; // once per 24 hours

const AppTopbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { company, plan, isPro, isOnTrial } = useCompany();

  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  const normalizedPlan = plan ? String(plan).toLowerCase() : "free";
  const planLabel =
    normalizedPlan.charAt(0).toUpperCase() + normalizedPlan.slice(1);

  // Decide if we should show the upgrade nudges
  useEffect(() => {
    // Pro users or active trial: no need to nag
    if (isPro || isOnTrial) {
      setShowUpgradePrompt(false);
      return;
    }

    try {
      const lastPrompt = localStorage.getItem(UPGRADE_PROMPT_KEY);
      if (!lastPrompt) {
        // Never shown before
        setShowUpgradePrompt(true);
        return;
      }

      const lastTime = Number(lastPrompt);
      if (Number.isNaN(lastTime)) {
        setShowUpgradePrompt(true);
        return;
      }

      const now = Date.now();
      if (now - lastTime > PROMPT_INTERVAL_MS) {
        // More than 24h since last prompt
        setShowUpgradePrompt(true);
      } else {
        setShowUpgradePrompt(false);
      }
    } catch {
      // If localStorage fails for any reason, default to showing it
      setShowUpgradePrompt(true);
    }
  }, [isPro, isOnTrial]);

  const handleGoToBilling = () => {
    // Record that we showed the prompt
    try {
      localStorage.setItem(UPGRADE_PROMPT_KEY, String(Date.now()));
    } catch {
      // ignore
    }
    navigate("/billings");
  };

  const handleDismissPrompt = () => {
    setShowUpgradePrompt(false);
    try {
      localStorage.setItem(UPGRADE_PROMPT_KEY, String(Date.now()));
    } catch {
      // ignore
    }
  };

  // You can make this smarter later (e.g. based on route name)
  const currentPathLabel = (() => {
    if (location.pathname.startsWith("/dashboard")) return "Dashboard";
    if (location.pathname.startsWith("/customers")) return "Customers";
    if (location.pathname.startsWith("/estimate")) return "Estimates";
    if (location.pathname.startsWith("/invoice")) return "Invoices";
    if (location.pathname.startsWith("/receipt")) return "Receipts";
    if (location.pathname.startsWith("/transactions")) return "Transactions";
    if (location.pathname.startsWith("/billings")) return "Billing";
    if (location.pathname.startsWith("/settings")) return "Setup";
    return "Workspace";
  })();

  return (
    <header className="app-topbar">
      <div className="topbar-left">
        <div className="topbar-title">
          <span className="topbar-kicker">KostPadi</span>
          <h2 className="topbar-page">{currentPathLabel}</h2>
        </div>
        {company?.company_name && (
          <div className="topbar-company">
            <span className="company-dot" />
            <span id="company-name">{company.company_name}</span>
          </div>
        )}
      </div>

      <div className="topbar-right">
        <div className={`plan-chip plan-${normalizedPlan}`}>
          <Crown size={14} />
          <span>{planLabel} plan</span>
          {isOnTrial && <span className="trial-tag">Trial</span>}
        </div>

        <NotificationsBell />
      </div>

      {/* Upgrade prompt banner: only for non-Pro users */}
      {showUpgradePrompt && (
        <div className="upgrade-banner">
          <div className="upgrade-text">
            <Crown size={16} />
            <span>
              Unlock full KostPadi power with <strong>Starter</strong> or{" "}
              <strong>Pro</strong> – access Transactions, Estimates, advanced
              reports and more.
            </span>
          </div>
          <div className="upgrade-actions">
            <button
              type="button"
              className="btn-ghost-small"
              onClick={handleDismissPrompt}
            >
              Not now
            </button>
            <button
              type="button"
              className="btn-upgrade"
              onClick={handleGoToBilling}
            >
              Upgrade plan <ArrowRight size={14} />
            </button>
            <button
              type="button"
              className="icon-btn small"
              onClick={handleDismissPrompt}
              aria-label="Dismiss"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default AppTopbar;
